import { ISetupCharge, ISetupChargeListParams } from "../../../shared/services/api/setupCharge/Types";
import { formatDateBR } from "../../../shared/utils/format-date";
import { globalFormatCurrencyBRL } from "../../../shared/utils/globalMask-document";
import { setupChargeFeePayerLabel, setupChargePaymentMethodLabel, setupChargeStatusLabel } from "./setupChargeStatus";

export const setupChargeFormatDate = (value?: string | null, withTime = false): string => {
	if (!value) return "";
	const formatted = formatDateBR(value, withTime);
	return formatted === "Invalid date" ? "" : formatted;
};

export const setupChargeFormatCurrency = (value?: number | string | null): string => {
	if (value === undefined || value === null || value === "") return "";
	return globalFormatCurrencyBRL(String(value));
};

/** Valor numérico seguro: texto vazio ou inválido vira `undefined`, nunca NaN na tela. */
export const setupChargeToNumber = (value?: number | string | null): number | undefined => {
	if (value === undefined || value === null || value === "") return undefined;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : undefined;
};

/** Hoje no formato `YYYY-MM-DD` (fuso local), para limitar campos de data. */
export const setupChargeToday = (): string => {
	const now = new Date();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${now.getFullYear()}-${month}-${day}`;
};

/** Filtros da aba como o usuário enxerga: a conversão para a API fica em `buildSetupChargeListParams`. */
export interface ISetupChargeFilterState {
	dateField: "created" | "paid";
	dateStart: string;
	dateEnd: string;
	status: string[];
	gateway: string;
	paymentMethod: string;
	clientId: string | null;
	search: string;
	duplicatePayment: boolean;
}

export const EMPTY_SETUP_CHARGE_FILTERS: ISetupChargeFilterState = {
	dateField: "created",
	dateStart: "",
	dateEnd: "",
	status: [],
	gateway: "",
	paymentMethod: "",
	clientId: null,
	search: "",
	duplicatePayment: false,
};

export const buildSetupChargeListParams = (filters: ISetupChargeFilterState, page: number, pageSize: number): ISetupChargeListParams => ({
	page: page + 1,
	pageSize,
	search: filters.search.trim(),
	status: filters.status.join(","),
	dateField: filters.dateField,
	dateStart: filters.dateStart,
	dateEnd: filters.dateEnd,
	clientId: filters.clientId ?? undefined,
	gateway: filters.gateway,
	paymentMethod: filters.paymentMethod,
	duplicatePayment: filters.duplicatePayment ? true : undefined,
});

// ------------------------------------------------------------------ exportação (CSV)

const csvCell = (value: unknown): string => {
	const text = value === undefined || value === null ? "" : String(value);
	return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const csvNumber = (value?: number | string | null): string => {
	const parsed = setupChargeToNumber(value);
	return parsed === undefined ? "" : parsed.toFixed(2).replace(".", ",");
};

export const SETUP_CHARGE_REPORT_HEADER = [
	"Data de pagamento",
	"Data de criação",
	"Imobiliária",
	"CNPJ/CPF da imobiliária",
	"Pagador",
	"Documento do pagador",
	"Valor cobrado",
	"Taxa do gateway",
	"Valor líquido",
	"Repasse esperado",
	"Repasse realizado",
	"Data do repasse",
	"Repasse",
	"Gateway",
	"Forma de pagamento",
	"Quem arca com a taxa",
	"Situação",
	"Pagamento em duplicidade",
	"Código da cobrança",
	"Código do parceiro",
];

export const setupChargeReportRow = (charge: ISetupCharge): string[] => [
	setupChargeFormatDate(charge.paidAt, true),
	setupChargeFormatDate(charge.createdAt, true),
	charge.clientName ?? "",
	charge.clientDocument ?? "",
	charge.payerName ?? "",
	charge.payerDocument ?? "",
	csvNumber(charge.amount),
	csvNumber(charge.gatewayFee),
	csvNumber(charge.netAmount),
	csvNumber(charge.expectedRepass),
	csvNumber(charge.repassAmount),
	setupChargeFormatDate(charge.repassedAt),
	charge.repassed ? "Repassado" : "Pendente",
	charge.gateway ?? "",
	setupChargePaymentMethodLabel(charge.paymentMethod),
	setupChargeFeePayerLabel(charge.snapshot?.feePayer),
	setupChargeStatusLabel(charge.status),
	charge.duplicatePayment ? "Sim" : "Não",
	charge.id,
	charge.externalId ?? "",
];

/**
 * CSV com `;` e BOM UTF-8: o Excel em português abre com os acentos certos e os valores em reais
 * (vírgula decimal) sem precisar importar.
 */
export const buildSetupChargeCsv = (charges: ISetupCharge[]): string => {
	const lines = [SETUP_CHARGE_REPORT_HEADER, ...charges.map(setupChargeReportRow)].map((line) => line.map(csvCell).join(";"));
	return `﻿${lines.join("\r\n")}`;
};

export const downloadSetupChargeCsv = (content: string, fileName: string): void => {
	const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8;" }));
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

/**
 * Data escolhida no campo de data (`YYYY-MM-DD`) como instante para a API. Meio-dia evita que o fuso
 * mostre o dia anterior; se for hoje, usa o instante atual (a API recusa pagamento no futuro).
 */
export const setupChargeDateToIso = (date: string): string => (date === setupChargeToday() ? new Date().toISOString() : `${date}T12:00:00.000Z`);
